import React, { useState, useEffect } from 'react';
import './DiceAnimationOverlay.css';

interface DiceAnimationOverlayProps {
  isVisible: boolean;
  stage: string;
  diceResult: any;
  onComplete: () => void;
}

const DiceAnimationOverlay: React.FC<DiceAnimationOverlayProps> = ({
  isVisible,
  stage,
  diceResult,
  onComplete
}) => {
  const [showResult, setShowResult] = useState(false);

  useEffect(() => {
    if (stage === 'revealing' && diceResult) {
      setShowResult(true);
      // Автоматически закрываем через 500мс после показа результата
      const timer = setTimeout(() => {
        onComplete();
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setShowResult(false);
    }
  }, [stage, diceResult, onComplete]);

  if (!isVisible) return null;

  // Получаем сообщение для текущего этапа
  const getStageMessage = () => {
    switch (stage) {
      case 'rolling':
        return 'Бросаем кубик...';
      case 'result_shown':
        return `Выпало: ${diceResult?.dice_roll || '?'} • Ваш проводник пишет...`;
      case 'error':
        return 'Что-то пошло не так...';
      default:
        return 'Магия в процессе...';
    }
  };

  // Получаем номер на кубике для отображения
  const getDiceNumber = () => {
    // Во время броска показываем случайные числа
    if (stage === 'rolling') {
      return Math.floor(Math.random() * 6) + 1;
    }
    
    // Во всех остальных состояниях показываем реальный результат API
    if (diceResult?.dice_roll) {
      return diceResult.dice_roll;
    }
    
    return 1; // По умолчанию показываем 1 (не должно случиться)
  };

  // Рендерим грань кубика с правильным количеством точек
  const renderDiceFace = (number: number | string) => {
    // Если число - знак вопроса, показываем текст
    if (number === '?') {
      return (
        <div className="dice-face-2d face-question">
          <span className="question-mark">?</span>
        </div>
      );
    }
    
    const dots = [];
    const num = typeof number === 'number' ? number : parseInt(number as string);
    
    // Создаем точки в зависимости от числа
    for (let i = 0; i < num; i++) {
      dots.push(<div key={i} className={`dice-dot dot-${num}-${i + 1}`} />);
    }
    
    return (
      <div className={`dice-face-2d face-${num}`}>
        {dots}
      </div>
    );
  };

  return (
    <div className="dice-overlay">
      <div className="dice-overlay-background" />
      
      <div className="dice-container">
        {/* 2D Кубик с правильными гранями */}
        <div className={`dice-2d ${stage}`}>
          {renderDiceFace(getDiceNumber())}
        </div>

        {/* Частицы вокруг кубика */}
        {(stage === 'rolling' || stage === 'result_shown') && (
          <div className={`dice-particles ${stage === 'result_shown' ? 'result-particles' : ''}`}>
            {[...Array(12)].map((_, i) => (
              <div key={i} className={`particle particle-${i + 1}`} />
            ))}
          </div>
        )}

        {/* Убрано: большая цифра поверх кубика */}
      </div>

      {/* Сообщение и прогресс */}
      <div className="dice-info">
        <div className="dice-message">{getStageMessage()}</div>
        
        {stage !== 'result_shown' && (
          <div className="dice-progress">
            <div className="progress-bar">
              <div className={`progress-fill ${stage}`} />
            </div>
            <div className="progress-dots">
              <span className={`dot ${['rolling', 'result_shown'].includes(stage) ? 'active' : ''}`} />
              <span className={`dot ${stage === 'result_shown' ? 'active' : ''}`} />
            </div>
          </div>
        )}
      </div>

      {/* Мистический фон */}
      <div className="mystical-background">
        <div className="cosmic-circle circle-1" />
        <div className="cosmic-circle circle-2" />
        <div className="cosmic-circle circle-3" />
      </div>
    </div>
  );
};

export default DiceAnimationOverlay;
